


import styled, { css } from 'styled-components';

import { colors, fonts } from '../../shared/tokens';
import { Section, Kicker } from '../landingShared';

const A = '/images/redesign/landing';

const PRODUCTS = [
  {
    num: '01',
    img: 'prod-ess.jpg',
    imageSide: 'left',
    title: 'ESS',
    subtitle: 'Electric Switch System',
    subtitleColor: colors.cyan,
    desc: 'Advanced electric track heating for railway switches and crossings. Keeps rail networks fully operational in the most extreme sub-zero conditions.',
    stats: [
      { value: '-40', unit: '°C', label: 'Operating range' },
      { value: '350', unit: '°C', label: 'Max output temp' },
    ],
  },
  {
    num: '02',
    img: 'prod-tgs.jpg',
    imageSide: 'right',
    line: 'Product Line 02',
    title: 'TGS',
    subtitle: 'Typhoon Gas Switch',
    subtitleColor: colors.red,
    desc: 'High-capacity gas-based heating for large switch installations and extreme weather. The industry benchmark for reliability and power output.',
    stats: [
      { value: '120', unit: 'kW', label: 'Peak output' },
      { value: '<90', unit: 's', label: 'Heat-up time' },
      { value: 'NFPA86', unit: '', label: 'Certified' },
    ],
  },
  {
    num: '03',
    img: 'prod-hps.jpg',
    imageSide: 'left',
    title: 'HPS',
    subtitle: 'Platform & Surface Heating',
    subtitleColor: '#f0a500',
    desc: 'Passenger platform and surface heating technology. Ensures safety and comfort in transit environments through the harshest winter operations.',
    stats: [
      { value: '200', unit: 'W/m²', label: 'Heat density' },
      { value: '30', unit: 'yr', label: 'Service life' },
      { value: 'ADA', unit: '', label: 'Certified' },
    ],
  },
];

const Products = () => (
  <Section>
    <Wrap>
      <Head>
        <Kicker>TEMPORA®</Kicker>
        <Heading>
          Industrial Heating
          <br />
          <em>Built for Extremes</em>
        </Heading>
        <Lead>
          Purpose-built hardware for the harshest transit and industrial
          environments — engineered to perform when it matters most.
        </Lead>
      </Head>

      <Cards>
        {PRODUCTS.map((p) => (
          <ProductCard key={p.num} $side={p.imageSide}>
            <Media $img={`${A}/${p.img}`} $side={p.imageSide} />
            <Body $side={p.imageSide}>
              {p.line && (
                <LineTag>
                  {p.line}
                  <LineDash />
                </LineTag>
              )}
              <Title>{p.title}</Title>
              <Subtitle style={{ color: p.subtitleColor }}>{p.subtitle}</Subtitle>
              <Desc>{p.desc}</Desc>
              <StatsRow>
                {p.stats.map((s, i) => (
                  <Stat key={s.label} $first={i === 0}>
                    <StatValue>
                      {s.value}
                      {s.unit && <StatUnit>{s.unit}</StatUnit>}
                    </StatValue>
                    <StatLabel>{s.label}</StatLabel>
                  </Stat>
                ))}
              </StatsRow>
              <LearnMore type="button">Learn More →</LearnMore>
            </Body>
            <Watermark $side={p.imageSide}>{p.num}</Watermark>
          </ProductCard>
        ))}
      </Cards>
    </Wrap>
  </Section>
);

export default Products;

const Wrap = styled.div`
  max-width: 1060px;
  margin: 0 auto;
  padding: 100px 80px;

  @media (max-width: 768px) {
    padding: 64px 24px;
  }
`;

const Head = styled.div`
  max-width: 680px;
`;

const Heading = styled.h2`
  margin-top: 18px;
  font-family: ${fonts.condensed};
  font-weight: 900;
  font-size: 52px;
  line-height: 52px;
  text-transform: uppercase;
  color: ${colors.ink};
  em {
    font-style: normal;
    color: ${colors.cyan};
  }
`;

const Lead = styled.p`
  margin-top: 16px;
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 16px;
  line-height: 28px;
  color: ${colors.muted};
`;

const Cards = styled.div`
  margin-top: 64px;
  display: flex;
  flex-direction: column;
  gap: 3px;
`;

const ProductCard = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: ${({ $side }) =>
    $side === 'left' ? '408px 1fr' : '1fr 408px'};
  min-height: 457px;
  background: #0d1420;
  border: 0.75px solid ${colors.hairline};
  overflow: hidden;
  transition: border-color 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    border-color: ${colors.cyanBorderSoft};
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.45);
  }

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    min-height: 0;
  }
`;

const Media = styled.div`
  position: relative;
  grid-row: 1;
  grid-column: ${({ $side }) => ($side === 'left' ? 1 : 2)};
  background-image: url(${({ $img }) => $img});
  background-size: cover;
  background-position: center;
  transition: transform 0.7s cubic-bezier(0.22, 1, 0.36, 1);
  will-change: transform;

  ${ProductCard}:hover & {
    transform: scale(1.05);
  }

  @media (prefers-reduced-motion: reduce) {
    ${ProductCard}:hover & {
      transform: none;
    }
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: ${({ $side }) =>
      $side === 'left'
        ? 'linear-gradient(to right, rgba(0,0,0,0) 60%, #0d1420 100%)'
        : 'linear-gradient(to left, rgba(0,0,0,0) 60%, #0d1420 100%)'};
  }

  @media (max-width: 820px) {
    grid-column: 1;
    grid-row: 1;
    min-height: 240px;
    &::after {
      background: linear-gradient(to top, #0d1420 0%, rgba(0, 0, 0, 0) 60%);
    }
  }
`;

const Body = styled.div`
  grid-row: 1;
  grid-column: ${({ $side }) => ($side === 'left' ? 2 : 1)};
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 48px 48px 48px 40px;

  @media (max-width: 820px) {
    grid-column: 1;
    grid-row: 2;
    padding: 32px 24px;
  }
`;

const LineTag = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
  font-family: ${fonts.mono};
  font-weight: 500;
  font-size: 11px;
  letter-spacing: 1.98px;
  text-transform: uppercase;
  color: ${colors.cyan};
`;

const LineDash = styled.span`
  width: 40px;
  height: 1px;
  background: #1e8a93;
`;

const Title = styled.h3`
  font-family: ${fonts.condensed};
  font-weight: 900;
  font-size: 52px;
  line-height: 52px;
  text-transform: uppercase;
  color: ${colors.ink};
`;

const Subtitle = styled.p`
  margin-top: 8px;
  font-family: ${fonts.body};
  font-weight: 500;
  font-size: 17px;
  line-height: 25.5px;
`;

const Desc = styled.p`
  margin-top: 20px;
  max-width: 402px;
  font-family: ${fonts.body};
  font-weight: 300;
  font-size: 15px;
  line-height: 24.75px;
  color: ${colors.muted};
`;

const StatsRow = styled.div`
  margin-top: 24px;
  display: flex;
  align-items: center;
`;

const Stat = styled.div`
  ${({ $first }) =>
    !$first &&
    css`
      margin-left: 24px;
      padding-left: 24px;
      border-left: 0.75px solid ${colors.hairline};
    `}
`;

const StatValue = styled.p`
  font-family: ${fonts.condensed};
  font-weight: 800;
  font-size: 28px;
  line-height: 42px;
  color: ${colors.ink};
`;

const StatUnit = styled.span`
  margin-left: 2px;
  font-family: ${fonts.body};
  font-weight: 800;
  font-size: 14px;
  color: ${colors.muted};
`;

const StatLabel = styled.p`
  margin-top: 2px;
  font-family: ${fonts.body};
  font-size: 11px;
  line-height: 16.5px;
  color: ${colors.muted2};
`;

const LearnMore = styled.button`
  align-self: flex-start;
  margin-top: 28px;
  padding: 11.75px 24.75px;
  border: 0.75px solid rgba(255, 255, 255, 0.15);
  border-radius: 3px;
  font-family: ${fonts.condensed};
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 1.3px;
  text-transform: uppercase;
  color: ${colors.ink};
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease;
  &:hover {
    border-color: ${colors.cyan};
    background: rgba(56, 200, 212, 0.06);
  }
`;

const Watermark = styled.span`
  position: absolute;
  top: 24px;
  ${({ $side }) => ($side === 'left' ? 'right: 34px;' : 'left: 338px;')}
  font-family: ${fonts.condensed};
  font-weight: 900;
  font-size: 120px;
  line-height: 120px;
  color: rgba(255, 255, 255, 0.02);
  pointer-events: none;
  user-select: none;

  @media (max-width: 820px) {
    display: none;
  }
`;
